import { Fragment, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import {
  IconAlertTriangle,
  IconArrowsLeftRight,
  IconAtom,
  IconBookmark,
  IconBuildingHospital,
  IconClipboardCheck,
  IconClock,
  IconFileText,
  IconLayoutGrid,
  IconPaw,
  IconShare2,
  IconStack2,
  IconTargetArrow,
  IconThumbDown,
  IconThumbUp,
  IconUsers,
  IconVaccine,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { Button, buttonVariants } from "../../components/ui/Button";
import { Logo } from "../../components/ui/Logo";
import { ImageLightbox } from "../../components/ui/ImageLightbox";
import type { Api } from "../../services/api";

type VisualAbstractView =
  Api["get"]["/webapp/public/visual-abstracts/:token"]["output"]["body"]["view"];

type VisualAbstractVisual = VisualAbstractView["visuals"][number];

type VisualAbstractPanel = VisualAbstractView["panels"][number];

// The poster owns the glyph for each semantic role the API declares. `satisfies`
// makes this exhaustive: a role added to the API enum fails this build until it
// is mapped here, so no panel can ever render without an icon.
const PANEL_ICONS = {
  population: IconUsers,
  intervention: IconVaccine,
  comparator: IconArrowsLeftRight,
  outcome: IconTargetArrow,
  study_design: IconLayoutGrid,
  evidence_base: IconStack2,
  setting: IconBuildingHospital,
  duration: IconClock,
  publication_type: IconFileText,
  species: IconPaw,
  safety: IconAlertTriangle,
  recommendation: IconClipboardCheck,
  mechanism: IconAtom,
  topic: IconBookmark,
} as const satisfies Record<VisualAbstractPanel["icon"], typeof IconUsers>;

type VisualAbstractActions =
  | { kind: "preview"; onUsefulness: UsefulnessHandler }
  | {
      kind: "public";
      articleUrl: string;
      reportUrl: string;
      // Null renders no nudge: this browser has logged in before, so the reader
      // already has an account and asking them to make one reads as a product
      // that does not know them. Nullable rather than optional so "no nudge" is
      // a state the caller has to decide, not one it can forget.
      signupUrl: string | null;
      onArticleClick: () => void;
      onSignupClick: () => void;
      onShare: () => void;
      onUsefulness: UsefulnessHandler;
    };

/**
 * Null renders no prompt at all — which is what the off-screen PNG export
 * passes. A poster snapshotted with a feedback control baked into the image
 * would ship a dead question to every slide deck it lands in.
 */
type UsefulnessHandler = ((useful: boolean) => void) | null;

type VisualAbstractPalette = {
  backgroundColor: string;
  primaryColor: string;
  secondaryColor: string;
};

function safeColor(value: string, fallback: string): string {
  return /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;
}

function visualAbstractPalette(
  view: VisualAbstractView
): VisualAbstractPalette {
  return {
    backgroundColor: safeColor(view.branding.backgroundColor, "#f6f5f3"),
    primaryColor: safeColor(view.branding.primaryColor, "#135345"),
    secondaryColor: safeColor(view.branding.secondaryColor, "#ffbe0a"),
  };
}

function brandedButtonStyle(palette: VisualAbstractPalette): CSSProperties {
  // `ds-btn-bg` paints [hover overlay, base colour]. Restate that stack with the
  // artifact's own primary as the base layer so room co-branding survives the DS
  // variant — and so the hover overlay declared by the class still applies.
  return {
    backgroundImage: `linear-gradient(var(--_ds-overlay), var(--_ds-overlay)), linear-gradient(${palette.primaryColor}, ${palette.primaryColor})`,
    borderColor: palette.primaryColor,
  };
}

function BentoGrid({
  children,
  className,
}: {
  children: ReactNode;
  className: string;
}): React.ReactElement {
  return <div className={`grid gap-3 ${className}`}>{children}</div>;
}

/**
 * Every block of the poster is its own card laid on the artifact background —
 * there is no single card wrapping the whole abstract.
 */
function BentoCard({
  children,
  className,
  palette,
  tone,
}: {
  children: ReactNode;
  className: string;
  palette: VisualAbstractPalette;
  tone: "brand" | "surface" | "muted";
}): React.ReactElement {
  const style: CSSProperties =
    tone === "brand"
      ? { backgroundColor: palette.primaryColor, color: "#ffffff" }
      : {};
  const toneClass =
    tone === "brand"
      ? "border-transparent"
      : tone === "surface"
        ? "border-transparent bg-white shadow-card"
        : "border-ds-moss-100 bg-ds-slate-50";
  return (
    <div
      className={`rounded-2xl border p-4 @lg/visual-abstract:p-5 ${toneClass} ${className}`}
      style={style}
    >
      {children}
    </div>
  );
}

function SectionLabel({
  children,
  palette,
}: {
  children: ReactNode;
  palette: VisualAbstractPalette;
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-center gap-2 text-caption font-extrabold uppercase tracking-wider">
      <span
        aria-hidden="true"
        className="size-2 shrink-0 rounded-full"
        style={{ backgroundColor: palette.secondaryColor }}
      />
      {children}
    </div>
  );
}

function panelGridClassName(panelCount: number): string {
  if (panelCount === 1) return "grid-cols-1";
  if (panelCount === 2) return "grid-cols-1 @lg/visual-abstract:grid-cols-2";
  if (panelCount === 3) return "grid-cols-1 @2xl/visual-abstract:grid-cols-3";
  if (panelCount === 4)
    return "grid-cols-1 @lg/visual-abstract:grid-cols-2 @2xl/visual-abstract:grid-cols-4";
  if (panelCount === 5)
    return "grid-cols-1 @lg/visual-abstract:grid-cols-2 @2xl/visual-abstract:grid-cols-6";
  throw new Error(
    `Visual abstract has invalid panel count ${String(panelCount)}`
  );
}

function panelCardClassName(panelCount: number, panelIndex: number): string {
  if (panelCount !== 5) return "";
  return panelIndex < 3
    ? "@2xl/visual-abstract:col-span-2"
    : "@2xl/visual-abstract:col-span-3";
}

type VisualAbstractRecommendations = NonNullable<
  Extract<VisualAbstractView, { family: "non_study" }>["recommendations"]
>;

/**
 * The guideline's recommendation table.
 *
 * A table and not a card list, because the grades are read as COLUMNS: an HCP
 * scanning a guideline poster looks down the Class column first and reads the
 * statements the strong ones attach to. It degrades to stacked rows under the
 * container breakpoint rather than scrolling sideways — a poster is read on a
 * phone, and a horizontally scrolling table hides the grade, which is the one
 * thing that must not be hidden.
 *
 * A statement with no grade prints an em dash rather than an empty cell: an
 * ungraded consensus recommendation is a real state, and a blank reads as a
 * rendering failure.
 */
function RecommendationTable({
  language,
  palette,
  recommendations,
}: {
  language: VisualAbstractView["language"];
  palette: VisualAbstractPalette;
  recommendations: VisualAbstractRecommendations;
}): React.ReactElement {
  const isGraded = recommendations.some(
    (recommendation) =>
      recommendation.recommendationClass !== null ||
      recommendation.levelOfEvidence !== null
  );
  return (
    <section className="@container/visual-panel min-w-0">
      <SectionLabel palette={palette}>
        {language === "fr" ? "Recommandations" : "Recommendations"}
      </SectionLabel>
      <BentoCard className="min-w-0" palette={palette} tone="surface">
        <ul className="m-0 flex list-none flex-col gap-4 p-0">
          {recommendations.map((recommendation) => (
            <li
              className="flex flex-col gap-2 border-b border-ds-border-btn pb-4 last:border-b-0 last:pb-0 @lg/visual-panel:flex-row @lg/visual-panel:items-baseline @lg/visual-panel:gap-5"
              key={recommendation.id}
            >
              {isGraded ? (
                <div className="flex shrink-0 gap-4 @lg/visual-panel:w-40 @lg/visual-panel:flex-col @lg/visual-panel:gap-1">
                  <RecommendationGrade
                    label={language === "fr" ? "Classe" : "Class"}
                    palette={palette}
                    value={recommendation.recommendationClass}
                  />
                  <RecommendationGrade
                    label={language === "fr" ? "Niveau de preuve" : "Level"}
                    palette={palette}
                    value={recommendation.levelOfEvidence}
                  />
                </div>
              ) : null}
              <p className="m-0 min-w-0 wrap-break-word text-ui-body leading-relaxed text-ds-slate-950">
                {recommendation.statement}
              </p>
            </li>
          ))}
        </ul>
      </BentoCard>
    </section>
  );
}

function RecommendationGrade({
  label,
  palette,
  value,
}: {
  label: string;
  palette: VisualAbstractPalette;
  value: string | null;
}): React.ReactElement {
  return (
    <div className="flex min-w-0 items-baseline gap-2 @lg/visual-panel:flex-col @lg/visual-panel:gap-0">
      <span className="text-caption font-extrabold uppercase tracking-wider text-ds-slate-600">
        {label}
      </span>
      {value === null ? (
        // An ungraded recommendation is a real state, so it prints a dash in the
        // brand's muted ink rather than an empty cell that reads as a bug.
        <strong className="font-dm-sans text-ui-highlight font-extrabold text-ds-slate-600">
          —
        </strong>
      ) : (
        <strong
          className="wrap-break-word font-dm-sans text-ui-highlight font-extrabold"
          style={{ color: palette.primaryColor }}
        >
          {value}
        </strong>
      )}
    </div>
  );
}

function ContextPanelGroup({
  palette,
  panels,
}: {
  palette: VisualAbstractPalette;
  panels: VisualAbstractView["panels"];
}): React.ReactElement {
  return (
    <BentoGrid className={panelGridClassName(panels.length)}>
      {panels.map((panel, panelIndex) => {
        // The glyph is keyed by the role the API declared, never by the free-text
        // label — renaming a label can never silently drop the icon.
        const PanelIcon = PANEL_ICONS[panel.icon];
        return (
          <BentoCard
            className={`min-w-0 ${panelCardClassName(panels.length, panelIndex)}`}
            key={panel.id}
            palette={palette}
            tone="muted"
          >
            <PanelIcon
              aria-hidden="true"
              className="mb-2 size-6"
              stroke={1.5}
            />
            <div className="mb-1 wrap-break-word text-footnote font-bold">
              {panel.label}
            </div>
            <p className="m-0 wrap-break-word text-ui-body leading-relaxed text-ds-slate-950">
              {panel.value}
            </p>
          </BentoCard>
        );
      })}
    </BentoGrid>
  );
}

/**
 * The display size a figure gets, derived from how long it is.
 *
 * A callout value is a free `string`: it can be an anchor token (`25%`) or an
 * interval (`97.2%–98.6%`). Rendering both at 60px is a promise the slot cannot
 * keep — the interval wrapped onto two lines and swallowed the card. Deriving
 * the size from the content is the only honest option, since nothing upstream
 * tells us which of the two we got. Thresholds are on the rendered string, so
 * this applies to already-stored artifacts with no regeneration.
 */
const METRIC_SIZES = [
  { maxLength: 5, className: "text-6xl/tight" },
  { maxLength: 8, className: "text-5xl/tight" },
  { maxLength: 14, className: "text-4xl/tight" },
] as const;

function metricSizeClassName(value: string): string {
  const size = METRIC_SIZES.find(
    (candidate) => value.length <= candidate.maxLength
  );
  return size === undefined ? "text-3xl/tight" : size.className;
}

function Metric({
  interval,
  label,
  palette,
  statistics,
  value,
}: {
  /** Null on a callout, which carries a bare figure and no estimate around it. */
  interval: string | null;
  label: string;
  palette: VisualAbstractPalette;
  statistics: string | null;
  value: string;
}): React.ReactElement {
  return (
    // Label above the figure, as on a poster stat block: the reader needs to
    // know what is measured before the number means anything.
    <div className="flex h-full flex-col justify-center">
      <span className="text-footnote font-semibold text-ds-slate-600">
        {label}
      </span>
      <strong
        className={`mt-1 wrap-break-word font-dm-sans font-extrabold ${metricSizeClassName(value)}`}
        style={{ color: palette.primaryColor }}
      >
        {value}
      </strong>
      {/* An estimate without its interval is not a smaller claim, it is a
          different one — so when the headline figure IS the effect estimate, the
          interval and the statistics come with it rather than staying behind in
          a section below the fold. */}
      {interval === null ? null : (
        <span className="mt-1 wrap-break-word text-ui-body font-semibold text-ds-slate-950">
          {interval}
        </span>
      )}
      {statistics === null ? null : (
        <span className="mt-1 wrap-break-word text-footnote text-ds-slate-600">
          {statistics}
        </span>
      )}
    </div>
  );
}

function VisualAbstractHeader({
  palette,
  titleLevel,
  view,
}: {
  palette: VisualAbstractPalette;
  titleLevel: "primary" | "secondary";
  view: VisualAbstractView;
}): React.ReactElement {
  const Title = titleLevel === "primary" ? "h1" : "h2";

  return (
    <header
      className="flex flex-col justify-between gap-5 rounded-2xl px-5 py-6 text-white @lg/visual-abstract:flex-row @lg/visual-abstract:px-8"
      style={{ backgroundColor: palette.primaryColor }}
    >
      <div className="min-w-0">
        <span className="inline-flex rounded-full bg-ds-mint-50 px-3 py-1 text-caption font-extrabold uppercase tracking-wider text-ds-mint-800">
          {view.header.sourceTypeLabel}
        </span>
        <Title className="mb-2 mt-4 max-w-2xl wrap-break-word font-dm-sans text-heading-lg font-bold @lg/visual-abstract:text-3xl/tight">
          {view.header.shortTitle}
        </Title>
        <p className="m-0 text-footnote text-ds-mint-50">
          {view.header.journalAndYear}
        </p>
      </div>

      {/* The lockup: an eyebrow over the mark, so the poster says WHO stands
          behind it instead of leaving a bare logo to be read as decoration.
          Attribution is by ORGANISATION, never by person — the artifact is
          publicly shareable, and naming the individual who generated it would
          publish personal data to anyone holding the link. The organisation is
          identified by its mark, which is already the Juisci logo or the room's
          own; nothing here is model-authored. */}
      <div className="flex shrink-0 flex-col items-start gap-2 @lg/visual-abstract:items-end @lg/visual-abstract:justify-center">
        <span className="text-caption font-extrabold uppercase tracking-wider text-ds-mint-50">
          {view.language === "fr" ? "Généré par" : "Generated by"}
        </span>
        <VisualAbstractBrandMark view={view} />
      </div>
    </header>
  );
}

/**
 * The brand on the poster is an image in both cases — never a name set in type.
 * A room's logo used to sit beside its name, and when the logo failed to render
 * the name was all that was left, so a co-branded poster read as the word
 * "SFCTCV" next to the Juisci mark.
 *
 * The mark comes from the design system's `Logo`, at `tone="white"` for the dark
 * header band. Which artwork that resolves to is the DS's business, not the
 * poster's: this file used to reach for the reversed asset itself, which is how
 * it could reach for the wrong one.
 */
function VisualAbstractBrandMark({
  view,
}: {
  view: VisualAbstractView;
}): React.ReactElement {
  switch (view.branding.source) {
    case "juisci":
      return <Logo tone="white" variant="no-tagline" />;
    case "room": {
      const logoAssetId = view.branding.logoAssetId;
      const asset = view.assets.find(
        (candidate) => candidate.id === logoAssetId
      );
      if (asset === undefined)
        throw new Error(
          `Visual abstract branding logo asset ${logoAssetId} is missing`
        );
      return (
        <img
          alt={view.branding.organisationName}
          className="max-h-14 max-w-36 rounded-xl bg-white object-contain p-2"
          src={asset.url}
        />
      );
    }
    default:
      throw new Error(view.branding satisfies never);
  }
}

function HeroSegment({
  palette,
  segment,
}: {
  palette: VisualAbstractPalette;
  segment: VisualAbstractView["hero"][number];
}): React.ReactElement {
  switch (segment.tone) {
    case "plain":
      return <>{segment.text}</>;
    case "emphasis":
      // `mark` carries the emphasis to assistive tech too. The tint is mixed
      // from the artifact's own primary, so room co-branding highlights in the
      // room's colour instead of a hardcoded one.
      return (
        <mark
          className="rounded px-1 text-inherit"
          style={{
            backgroundColor: `color-mix(in srgb, ${palette.primaryColor} 14%, transparent)`,
          }}
        >
          {segment.text}
        </mark>
      );
    default:
      throw new Error(segment.tone satisfies never);
  }
}

/**
 * The figures printed at display size beside the hero sentence.
 *
 * A study's headline figure is its PRIMARY result, always. Callouts are by
 * construction everything the primary result is NOT — the briefing prompt
 * forbids a callout from repeating a number the effect estimate already
 * carries — so headlining them made the poster lead with a secondary or safety
 * outcome while the primary endpoint sat below the fold: a pacemaker rate of
 * "44.7% vs 14.0%" printed over "no significant difference in all-cause
 * mortality, HR 1.0 (95% CI 0.7–1.3)". Medical review flagged it on two
 * different artifacts, once as a heterogeneity stat (I² = 99%) and once as a
 * safety stat, which is the same defect twice.
 *
 * A source with no primary effect — original research reporting no comparison,
 * a guideline, a review — has no better figure to lead with, so its callouts
 * keep the slot.
 */
type VisualAbstractHeadline =
  | { kind: "effect"; effect: VisualAbstractEffect }
  | { kind: "callouts"; callouts: VisualAbstractView["callouts"] };

function visualAbstractHeadline(
  view: VisualAbstractView
): VisualAbstractHeadline {
  if (view.family === "study" && view.primaryEffect !== null)
    return { kind: "effect", effect: view.primaryEffect };
  return { kind: "callouts", callouts: view.callouts };
}

function HeroBento({
  palette,
  view,
}: {
  palette: VisualAbstractPalette;
  view: VisualAbstractView;
}): React.ReactElement {
  const headline = visualAbstractHeadline(view);
  // Both arms render as the same poster stat block, so they are flattened to
  // one list here rather than branching the layout twice: the column spans are
  // a function of HOW MANY figures sit beside the sentence, not of where they
  // came from.
  const metrics =
    headline.kind === "effect"
      ? [
          {
            id: headline.effect.id,
            label: headline.effect.label,
            value: headline.effect.value,
            interval: headline.effect.interval,
            statistics: headline.effect.statistics,
          },
        ]
      : headline.callouts.map((callout) => ({
          id: callout.id,
          label: callout.label,
          value: callout.value,
          interval: null,
          statistics: null,
        }));
  const firstCallout = metrics[0];
  const secondCallout = metrics[1];
  const heroClassName =
    metrics.length === 0
      ? "@2xl/visual-abstract:col-span-12"
      : metrics.length === 1
        ? "@2xl/visual-abstract:col-span-8"
        : "@2xl/visual-abstract:col-span-6";
  const calloutClassName =
    metrics.length === 1
      ? "@2xl/visual-abstract:col-span-4"
      : "@2xl/visual-abstract:col-span-3";

  // The headline result is ONE card holding its figures and its sentence, not
  // three cards in a row: as separate grid items the short metric cards
  // stretched to the height of the sentence and floated their value in a tall
  // empty box. Inside a single card the columns are `items-center`, so a figure
  // sits against its sentence instead of being padded out to match it.
  return (
    <BentoCard className="min-w-0" palette={palette} tone="surface">
      <div className="grid grid-cols-1 gap-5 @2xl/visual-abstract:grid-cols-12 @2xl/visual-abstract:items-center @2xl/visual-abstract:gap-6">
        {firstCallout === undefined ? null : (
          <div className={`min-w-0 ${calloutClassName}`}>
            <Metric
              interval={firstCallout.interval}
              label={firstCallout.label}
              palette={palette}
              statistics={firstCallout.statistics}
              value={firstCallout.value}
            />
          </div>
        )}
        <p
          className={`m-0 min-w-0 wrap-break-word font-dm-sans text-ui-highlight text-ds-slate-950 ${heroClassName}`}
        >
          {view.hero.map((segment, segmentIndex) => (
            <HeroSegment
              key={`${String(segmentIndex)}-${segment.text}`}
              palette={palette}
              segment={segment}
            />
          ))}
        </p>
        {secondCallout === undefined ? null : (
          <div className={`min-w-0 ${calloutClassName}`}>
            <Metric
              interval={secondCallout.interval}
              label={secondCallout.label}
              palette={palette}
              statistics={secondCallout.statistics}
              value={secondCallout.value}
            />
          </div>
        )}
      </div>
    </BentoCard>
  );
}

type VisualAbstractEffect = Extract<
  VisualAbstractView,
  { family: "study" }
>["secondaryEffects"][number];

/**
 * The estimate, its interval and its statistics on one card.
 *
 * They arrive as three fields precisely so they can be sized as three things: an
 * HCP reads the point estimate first, then checks whether the interval crosses
 * the null, then looks at what qualifies it. Joining them into one string
 * upstream would have made that hierarchy impossible to express, and printing
 * the estimate without the interval would ship a different claim entirely.
 *
 * Every card here is a SUPPORTING result: the primary effect is the poster's
 * headline figure and is rendered in the hero, so nothing in this section is
 * ever the paper's main finding and nothing needs a second emphasis level.
 */
function EffectCard({
  effect,
  palette,
}: {
  effect: VisualAbstractEffect;
  palette: VisualAbstractPalette;
}): React.ReactElement {
  return (
    <BentoCard className="flex min-w-0 flex-col" palette={palette} tone="muted">
      <span className="wrap-break-word text-footnote font-semibold text-ds-slate-600">
        {effect.label}
      </span>
      <strong
        className="mt-1 wrap-break-word font-dm-sans text-2xl/tight font-extrabold @md/visual-panel:text-3xl/tight"
        style={{ color: palette.primaryColor }}
      >
        {effect.value}
      </strong>
      {effect.interval === null ? null : (
        <span className="mt-1 wrap-break-word text-ui-body font-semibold text-ds-slate-950">
          {effect.interval}
        </span>
      )}
      {effect.statistics === null ? null : (
        <span className="mt-1 wrap-break-word text-footnote text-ds-slate-600">
          {effect.statistics}
        </span>
      )}
    </BentoCard>
  );
}

/**
 * The results that sit BELOW the headline figure: the source's secondary and
 * safety outcomes, plus the callouts the hero demoted when it took the primary
 * effect.
 *
 * `callouts` is a prop rather than read off `view` because the poster owns the
 * arbitration: a callout is here only when the hero did NOT print it, and both
 * decisions come from the same `visualAbstractHeadline` call, so a callout can
 * never be dropped from the poster or printed on it twice.
 */
function EffectGroup({
  callouts,
  palette,
  view,
}: {
  callouts: VisualAbstractView["callouts"];
  palette: VisualAbstractPalette;
  view: Extract<VisualAbstractView, { family: "study" }>;
}): React.ReactElement | null {
  if (callouts.length === 0 && view.secondaryEffects.length === 0) return null;
  return (
    <section className="@container/visual-panel min-w-0">
      <SectionLabel palette={palette}>
        {view.language === "fr" ? "Autres résultats" : "Other results"}
      </SectionLabel>
      <BentoGrid className="grid-cols-1 @md/visual-panel:grid-cols-2">
        {callouts.map((callout) => (
          <EffectCard
            effect={{
              id: callout.id,
              label: callout.label,
              value: callout.value,
              interval: null,
              statistics: null,
            }}
            key={callout.id}
            palette={palette}
          />
        ))}
        {view.secondaryEffects.map((effect) => (
          <EffectCard effect={effect} key={effect.id} palette={palette} />
        ))}
      </BentoGrid>
    </section>
  );
}

type VisualAbstractForestPlot = Extract<
  VisualAbstractVisual,
  { kind: "forest_plot" }
>;

type VisualAbstractPathway = Extract<VisualAbstractVisual, { kind: "pathway" }>;

/**
 * The axis ladder a forest plot is drawn against.
 *
 * Ticks come from a FIXED ladder rather than from the data, because a reader
 * compares plots across posters: an axis that rescaled to each study's range
 * would put the null line in a different place every time and make two posters
 * silently incomparable. We take the narrowest ladder span containing every
 * bound, so the geometry is a pure function of the numbers.
 */
const RATIO_TICKS = [0.1, 0.2, 0.5, 0.7, 1, 1.5, 2, 5, 10] as const;

function forestAxis(plot: VisualAbstractForestPlot): {
  ticks: number[];
  min: number;
  max: number;
  nullValue: number;
} {
  const bounds = [
    ...plot.rows.flatMap((row) => [row.lower, row.upper]),
    plot.pooled.lower,
    plot.pooled.upper,
  ];
  const lowest = Math.min(...bounds);
  const highest = Math.max(...bounds);
  if (plot.scale === "ratio") {
    const ticks = RATIO_TICKS.filter(
      (tick, tickIndex) =>
        // Keep a tick when it is inside the data range, plus the one on each
        // side of it, so the extremes are never clipped against the edge.
        (tick >= lowest && tick <= highest) ||
        (RATIO_TICKS[tickIndex + 1] !== undefined &&
          RATIO_TICKS[tickIndex + 1] > lowest &&
          tick < lowest) ||
        (RATIO_TICKS[tickIndex - 1] !== undefined &&
          RATIO_TICKS[tickIndex - 1] < highest &&
          tick > highest)
    );
    const min = ticks[0];
    const max = ticks[ticks.length - 1];
    if (min === undefined || max === undefined)
      throw new Error("Invariant: forest plot axis has no tick inside range");
    return { ticks, min, max, nullValue: 1 };
  }
  // A difference is centred on zero and scaled symmetrically, so the null line
  // stays in the middle and "favours" reads the same way on both sides.
  const reach = Math.max(Math.abs(lowest), Math.abs(highest)) * 1.1;
  const step = reach / 2;
  return {
    ticks: [-reach, -step, 0, step, reach],
    min: -reach,
    max: reach,
    nullValue: 0,
  };
}

/**
 * Position along the track, 0–100.
 *
 * A ratio is plotted on a LOG axis — that is not decoration: on a linear axis a
 * halving (0.5) and a doubling (2) sit at wildly different distances from 1, so
 * the plot would misstate the symmetry of the effect.
 */
function forestPosition(
  value: number,
  axis: ReturnType<typeof forestAxis>,
  scale: VisualAbstractForestPlot["scale"]
): number {
  const clamped = Math.min(Math.max(value, axis.min), axis.max);
  const fraction =
    scale === "ratio"
      ? (Math.log(clamped) - Math.log(axis.min)) /
        (Math.log(axis.max) - Math.log(axis.min))
      : (clamped - axis.min) / (axis.max - axis.min);
  return fraction * 100;
}

function ForestRowTrack({
  axis,
  palette,
  plot,
  row,
}: {
  axis: ReturnType<typeof forestAxis>;
  palette: VisualAbstractPalette;
  plot: VisualAbstractForestPlot;
  row: VisualAbstractForestPlot["rows"][number];
}): React.ReactElement {
  const left = forestPosition(row.lower, axis, plot.scale);
  const right = forestPosition(row.upper, axis, plot.scale);
  const centre = forestPosition(row.estimate, axis, plot.scale);
  // Weights are usually absent (they live inside the published figure), so the
  // square falls back to one uniform size rather than encoding a made-up one.
  const side = row.weight === null ? 10 : 7 + (row.weight / 100) * 12;
  return (
    <div className="relative h-6">
      <div
        className="absolute top-1/2 h-0.5 -translate-y-1/2 rounded-full"
        style={{
          left: `${String(left)}%`,
          width: `${String(Math.max(right - left, 0.5))}%`,
          backgroundColor: palette.primaryColor,
        }}
      />
      <div
        className="absolute top-1/2 -translate-1/2 rounded-sm"
        style={{
          left: `${String(centre)}%`,
          width: `${String(side)}px`,
          height: `${String(side)}px`,
          backgroundColor: palette.primaryColor,
        }}
      />
    </div>
  );
}

/**
 * The concept / algorithm diagram: boxes the reader walks down, forks laid out
 * side by side under the condition that selects them.
 *
 * Stacked vertically at EVERY width, deliberately. A left-to-right flow reads
 * well until a fork, where the branches have to stack anyway and the arrows cross
 * — and the poster is a portrait document that already scrolls. Vertical also
 * reflows for free, which is the whole reason the design prefers a rebuilt
 * schematic to a published figure.
 *
 * Semantically an ordered list, so the sequence reaches assistive tech without
 * depending on the connectors; the connectors are `aria-hidden` decoration. A
 * fork's branches are an unordered list inside their step: they are alternatives,
 * not a next step.
 */
function PathwayPanel({
  className,
  palette,
  pathway,
}: {
  className: string;
  palette: VisualAbstractPalette;
  pathway: VisualAbstractPathway;
}): React.ReactElement {
  return (
    <section className={`@container/visual-panel min-w-0 ${className}`}>
      <BentoCard className="min-w-0" palette={palette} tone="surface">
        <h3
          className="mb-4 mt-0 wrap-break-word font-dm-sans text-heading-sm font-bold"
          style={{ color: palette.primaryColor }}
        >
          {pathway.title}
        </h3>
        <ol className="m-0 flex list-none flex-col items-stretch gap-0 p-0">
          {pathway.steps.map((step, stepIndex) => (
            <li className="min-w-0" key={step.id}>
              {stepIndex === 0 ? null : (
                <div
                  aria-hidden="true"
                  className="mx-auto h-6 w-px"
                  style={{ backgroundColor: palette.primaryColor }}
                />
              )}
              <PathwayStepBoxes palette={palette} step={step} />
            </li>
          ))}
        </ol>
      </BentoCard>
    </section>
  );
}

function PathwayStepBoxes({
  palette,
  step,
}: {
  palette: VisualAbstractPalette;
  step: VisualAbstractPathway["steps"][number];
}): React.ReactElement {
  const stepShape = step.shape;
  switch (stepShape) {
    case "step": {
      const [node] = step.nodes;
      if (node === undefined)
        throw new Error("Invariant: a pathway step carries no node");
      return <PathwayBox node={node} palette={palette} />;
    }
    case "fork":
      return (
        <ul className="m-0 grid list-none grid-cols-1 gap-3 p-0 @sm/visual-panel:grid-cols-2 @lg/visual-panel:grid-cols-3">
          {step.nodes.map((node) => (
            <li className="min-w-0" key={node.id}>
              <PathwayBox node={node} palette={palette} />
            </li>
          ))}
        </ul>
      );
    default:
      throw new Error(stepShape satisfies never);
  }
}

/**
 * One box. The condition is printed ABOVE the text, as a label rather than a
 * sentence: it is what selects this branch, and folding it into the box's prose
 * ("if high risk, implant a defibrillator") turns a decision point into a
 * paragraph — which is the thing this primitive exists to replace.
 */
function PathwayBox({
  node,
  palette,
}: {
  node: VisualAbstractPathway["steps"][number]["nodes"][number];
  palette: VisualAbstractPalette;
}): React.ReactElement {
  return (
    <BentoCard className="h-full min-w-0" palette={palette} tone="muted">
      {node.condition === null ? null : (
        <span
          className="mb-1 block wrap-break-word text-caption font-extrabold uppercase tracking-wider"
          style={{ color: palette.primaryColor }}
        >
          {node.condition}
        </span>
      )}
      <span className="block wrap-break-word text-ui-body font-semibold text-ds-slate-950">
        {node.text}
      </span>
    </BentoCard>
  );
}

function ForestPlotPanel({
  className,
  palette,
  plot,
  language,
}: {
  className: string;
  palette: VisualAbstractPalette;
  plot: VisualAbstractForestPlot;
  language: VisualAbstractView["language"];
}): React.ReactElement {
  const axis = forestAxis(plot);
  const nullPosition = forestPosition(axis.nullValue, axis, plot.scale);
  const pooledLeft = forestPosition(plot.pooled.lower, axis, plot.scale);
  const pooledRight = forestPosition(plot.pooled.upper, axis, plot.scale);
  const interval = (lower: number, upper: number): string =>
    `${String(lower)}–${String(upper)}`;

  return (
    <section className={`@container/visual-panel min-w-0 ${className}`}>
      <BentoCard className="min-w-0" palette={palette} tone="surface">
        <h3
          className="mb-4 mt-0 wrap-break-word font-dm-sans text-heading-sm font-bold"
          style={{ color: palette.primaryColor }}
        >
          {plot.title}
        </h3>

        {/* The track gets the elastic column and the text columns are sized to
            their content: given `1fr` the labels swallowed the width and the
            plot — the only part that needs room to be read — was squeezed into
            a third of the card. */}
        <div className="grid grid-cols-[minmax(0,max-content)_minmax(0,1fr)] items-center gap-x-3 gap-y-1 @md/visual-panel:grid-cols-[minmax(0,max-content)_minmax(0,1fr)_max-content] @md/visual-panel:gap-x-4">
          {plot.rows.map((row) => (
            <Fragment key={row.id}>
              <div className="min-w-0">
                <div className="wrap-break-word text-footnote font-semibold text-ds-slate-950">
                  {row.label}
                </div>
                {row.sublabel === null ? null : (
                  <div className="wrap-break-word text-footnote text-ds-slate-600">
                    {row.sublabel}
                  </div>
                )}
              </div>
              {/* The null line is redrawn per row so it reads as one continuous
                  rule without an overlay that would have to match row heights. */}
              <div className="relative min-w-0">
                <div
                  aria-hidden="true"
                  className="absolute inset-y-0 w-px border-l border-dashed border-ds-slate-400"
                  style={{ left: `${String(nullPosition)}%` }}
                />
                <ForestRowTrack
                  axis={axis}
                  palette={palette}
                  plot={plot}
                  row={row}
                />
              </div>
              <div className="col-span-2 -mt-1 mb-2 text-footnote tabular-nums text-ds-slate-600 @md/visual-panel:col-span-1 @md/visual-panel:my-0 @md/visual-panel:text-right">
                {String(row.estimate)} ({interval(row.lower, row.upper)})
                {row.weight === null ? null : (
                  <span className="ml-2 text-ds-slate-400">
                    {String(row.weight)}%
                  </span>
                )}
              </div>
            </Fragment>
          ))}

          <div className="min-w-0 wrap-break-word text-footnote font-bold text-ds-slate-950">
            {language === "fr" ? "Global (poolé)" : "Overall (pooled)"}
          </div>
          <div className="relative min-w-0">
            <div
              aria-hidden="true"
              className="absolute inset-y-0 w-px border-l border-dashed border-ds-slate-400"
              style={{ left: `${String(nullPosition)}%` }}
            />
            <div className="relative h-6">
              <div
                className="absolute top-1/2 h-3 -translate-y-1/2"
                style={{
                  left: `${String(pooledLeft)}%`,
                  width: `${String(Math.max(pooledRight - pooledLeft, 1.5))}%`,
                  backgroundColor: palette.primaryColor,
                  clipPath: "polygon(0 50%, 50% 0, 100% 50%, 50% 100%)",
                }}
              />
            </div>
          </div>
          <div className="col-span-2 text-footnote font-bold tabular-nums text-ds-slate-950 @md/visual-panel:col-span-1 @md/visual-panel:text-right">
            {String(plot.pooled.estimate)} (
            {interval(plot.pooled.lower, plot.pooled.upper)})
          </div>

          {/* The axis sits in the TRACK column at every width. Spanning it
              across the label column too put `0.5` under the trial names while
              the plot started a third of the way in — an axis that does not
              line up with its own marks. */}
          <div aria-hidden="true" />
          <div className="relative mt-2 h-4">
            {axis.ticks.map((tick) => (
              <span
                className="absolute -translate-x-1/2 text-caption tabular-nums text-ds-slate-600"
                key={tick}
                style={{
                  left: `${String(forestPosition(tick, axis, plot.scale))}%`,
                }}
              >
                {String(tick)}
              </span>
            ))}
          </div>
          <div aria-hidden="true" className="hidden @md/visual-panel:block" />
        </div>

        <div className="mt-3 flex justify-between gap-3 text-caption text-ds-slate-600">
          <span className="wrap-break-word">← {plot.favoursLower}</span>
          <span className="wrap-break-word text-right">
            {plot.favoursHigher} →
          </span>
        </div>
      </BentoCard>
    </section>
  );
}

/**
 * A source figure, a forest plot and a pathway are excluded by type, not by an
 * unreachable `case`: each heads its card with its own title, so there is no
 * generic label to return for them and asking for one is a programming error.
 */
function visualLabel(
  visual: Exclude<
    VisualAbstractVisual,
    { kind: "source_figure" } | { kind: "forest_plot" } | { kind: "pathway" }
  >,
  language: VisualAbstractView["language"]
): string {
  switch (visual.kind) {
    case "stat_tiles":
      return language === "fr" ? "Résultats clés" : "Key results";
    case "timeline":
      return language === "fr" ? "Chronologie" : "Timeline";
    case "recommendations":
      return language === "fr" ? "Recommandations" : "Recommendations";
    case "themes":
      return language === "fr" ? "Thèmes clés" : "Key themes";
    default:
      throw new Error(visual satisfies never);
  }
}

function VisualPanel({
  className,
  palette,
  view,
  visual,
}: {
  className: string;
  palette: VisualAbstractPalette;
  view: VisualAbstractView;
  visual: VisualAbstractVisual;
}): React.ReactElement {
  switch (visual.kind) {
    case "forest_plot":
      return (
        <ForestPlotPanel
          className={className}
          language={view.language}
          palette={palette}
          plot={visual}
        />
      );
    case "pathway":
      return (
        <PathwayPanel
          className={className}
          palette={palette}
          pathway={visual}
        />
      );
    case "source_figure": {
      const asset = view.assets.find(
        (candidate) => candidate.id === visual.assetId
      );
      return (
        <BentoCard
          className={`min-w-0 ${className}`}
          palette={palette}
          tone="surface"
        >
          <figure className="@container/visual-panel m-0">
            <h3
              className="mb-3 mt-0 wrap-break-word font-dm-sans text-heading-sm font-bold"
              style={{ color: palette.primaryColor }}
            >
              {visual.title}
            </h3>
            {asset === undefined ? (
              <div className="grid min-h-72 place-items-center rounded-xl bg-ds-moss-100 p-4 text-center font-semibold text-ds-slate-600">
                {view.language === "fr"
                  ? "Figure source indisponible"
                  : "Source figure unavailable"}
              </div>
            ) : (
              // Tappable: a dense chart is unreadable at poster width on a
              // phone, and the enlarged view is also where the full legend
              // fits.
              <ImageLightbox
                className="mx-auto max-h-screen rounded-xl bg-white object-contain"
                src={asset.url}
                alt={asset.altText}
                openLabel={
                  view.language === "fr"
                    ? "Agrandir la figure"
                    : "Enlarge the figure"
                }
                closeLabel={view.language === "fr" ? "Fermer" : "Close"}
                detail={
                  asset.sourceCaption === null ? null : (
                    <p className="m-0 wrap-break-word text-caption leading-relaxed text-ds-slate-600">
                      {asset.figureLabel === null ? null : (
                        <span className="font-bold">
                          {asset.figureLabel} —{" "}
                        </span>
                      )}
                      {asset.sourceCaption}
                    </p>
                  )
                }
              />
            )}
            {/* The publisher's own number and the publisher's own legend, both
                verbatim — the reader's way back to the paper. The legend used to
                be a sentence the MODEL wrote, and whatever sits under a figure
                is read as that figure's legend, so a reworded one put words in
                the publisher's mouth. Clamped rather than truncated server-side:
                half of these run past 400 characters, the full text stays in the
                DOM for assistive tech, and the dialog above shows all of it. */}
            <figcaption className="mt-3 line-clamp-3 wrap-break-word text-caption leading-relaxed text-ds-slate-600">
              {asset === undefined || asset.figureLabel === null ? null : (
                <span className="font-bold">{asset.figureLabel} — </span>
              )}
              {asset === undefined ? null : asset.sourceCaption}
            </figcaption>
            {visual.readouts.length === 0 ? null : (
              <BentoGrid className="mt-4 grid-cols-1">
                {visual.readouts.map((readout) => (
                  <BentoCard
                    className="min-w-0"
                    key={readout.id}
                    palette={palette}
                    tone="muted"
                  >
                    <div className="wrap-break-word text-ui-body font-bold text-ds-slate-950">
                      {readout.label}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-8 gap-y-3">
                      {readout.values.map((value) => (
                        <div className="min-w-0" key={value.label}>
                          <div className="wrap-break-word text-caption text-ds-slate-600">
                            {value.label}
                          </div>
                          {/* Sized like a stat tile, not like the hero figure:
                              a readout is one of several readings of a panel,
                              so it must not outrank the poster's anchor. */}
                          <strong
                            className="wrap-break-word text-3xl/tight font-extrabold @md/visual-panel:text-4xl/tight"
                            style={{ color: palette.primaryColor }}
                          >
                            {value.value}
                          </strong>
                        </div>
                      ))}
                    </div>
                    {readout.detail === null ? null : (
                      <p className="m-0 mt-3 wrap-break-word text-caption text-ds-slate-600">
                        {readout.detail}
                      </p>
                    )}
                  </BentoCard>
                ))}
              </BentoGrid>
            )}
          </figure>
        </BentoCard>
      );
    }
    case "stat_tiles":
      return (
        // Own container: a panel squeezed beside a source figure must count its
        // columns from its own width, not from the poster's.
        <section className={`@container/visual-panel min-w-0 ${className}`}>
          <SectionLabel palette={palette}>
            {visualLabel(visual, view.language)}
          </SectionLabel>
          <BentoGrid className="grid-cols-1 @md/visual-panel:grid-cols-2">
            {visual.results.map((result, resultIndex) => (
              <BentoCard
                className={`flex min-w-0 flex-col ${
                  visual.results.length > 1 &&
                  visual.results.length % 2 === 1 &&
                  resultIndex === visual.results.length - 1
                    ? "@md/visual-panel:col-span-2"
                    : ""
                }`}
                key={result.id}
                palette={palette}
                tone="muted"
              >
                <strong
                  className="wrap-break-word text-3xl/tight font-extrabold @md/visual-panel:text-4xl/tight"
                  style={{ color: palette.primaryColor }}
                >
                  {result.value}
                </strong>
                <span className="mt-2 wrap-break-word text-footnote text-ds-slate-600">
                  {result.label}
                </span>
              </BentoCard>
            ))}
          </BentoGrid>
        </section>
      );
    case "timeline":
    case "recommendations":
    case "themes": {
      return (
        <section className={`@container/visual-panel min-w-0 ${className}`}>
          <SectionLabel palette={palette}>
            {visualLabel(visual, view.language)}
          </SectionLabel>
          <ol className="m-0 grid list-none grid-cols-1 gap-3 p-0 @md/visual-panel:grid-cols-2">
            {visual.items.map((item, itemIndex) => (
              <li
                className={
                  visual.items.length > 1 &&
                  visual.items.length % 2 === 1 &&
                  itemIndex === visual.items.length - 1
                    ? "@md/visual-panel:col-span-2"
                    : ""
                }
                key={item.id}
              >
                <BentoCard
                  className="h-full min-w-0"
                  palette={palette}
                  tone="muted"
                >
                  <strong className="block wrap-break-word text-ui-body font-semibold">
                    {item.label}
                  </strong>
                  <span className="mt-1 block wrap-break-word text-footnote leading-relaxed text-ds-slate-600">
                    {item.value}
                  </span>
                </BentoCard>
              </li>
            ))}
          </ol>
        </section>
      );
    }
    default:
      throw new Error(visual satisfies never);
  }
}

function visualsGridClassName(visualCount: number): string {
  // `items-start` keeps each panel at its own height instead of stretching the
  // shorter one — a figure card must not grow blank space to match its neighbour.
  return visualCount === 1
    ? "grid-cols-1"
    : "grid-cols-1 items-start @2xl/visual-abstract:grid-cols-6";
}

function visualCardClassName(
  visuals: VisualAbstractView["visuals"],
  visual: VisualAbstractVisual
): string {
  if (visuals.length === 1) return "";
  // A forest plot is a chart with an axis: squeezed into half a row its ticks
  // collide and the null line stops being readable, so it takes the full width
  // and its companion drops below it.
  if (visual.kind === "forest_plot") return "@2xl/visual-abstract:col-span-6";
  if (visuals.some((candidate) => candidate.kind === "forest_plot"))
    return "@2xl/visual-abstract:col-span-6";
  // A reproduced source figure has to stay legible, so it takes two thirds of
  // the row and its companion panel takes the remaining third.
  const figureCount = visuals.filter(
    (candidate) => candidate.kind === "source_figure"
  ).length;
  // TWO reproduced figures side by side is half a row each, and a publisher's
  // forest plot at that width is a grey smudge — its per-study rows and its
  // axis are the content. They stack full-width instead: a taller poster beats
  // an unreadable one.
  if (figureCount === 2) return "@2xl/visual-abstract:col-span-6";
  if (figureCount !== 1) return "@2xl/visual-abstract:col-span-3";
  return visual.kind === "source_figure"
    ? "@2xl/visual-abstract:col-span-4"
    : "@2xl/visual-abstract:col-span-2";
}

function VisualAbstractPoster({
  palette,
  view,
}: {
  palette: VisualAbstractPalette;
  view: VisualAbstractView;
}): React.ReactElement {
  return (
    <article className="flex flex-col gap-3">
      <VisualAbstractHeader
        palette={palette}
        titleLevel="primary"
        view={view}
      />

      <HeroBento palette={palette} view={view} />

      {view.family === "study" ? (
        <EffectGroup
          callouts={
            visualAbstractHeadline(view).kind === "effect" ? view.callouts : []
          }
          palette={palette}
          view={view}
        />
      ) : null}

      {/* A guideline's recommendations sit directly under the hero, ahead of the
          context panels: for a guideline they ARE the paper. */}
      {view.family === "non_study" && view.recommendations !== null ? (
        <RecommendationTable
          language={view.language}
          palette={palette}
          recommendations={view.recommendations}
        />
      ) : null}

      <ContextPanelGroup palette={palette} panels={view.panels} />

      {/* Deduplication can legitimately leave a study with no visual panel — an
          empty grid would still lay out its gap between the neighbours. */}
      {view.visuals.length === 0 ? null : (
        <BentoGrid className={visualsGridClassName(view.visuals.length)}>
          {view.visuals.map((visual) => (
            <VisualPanel
              className={visualCardClassName(view.visuals, visual)}
              key={visual.id}
              palette={palette}
              view={view}
              visual={visual}
            />
          ))}
        </BentoGrid>
      )}

      <BentoCard className="min-w-0" palette={palette} tone="brand">
        <h2
          className="mb-2 mt-0 text-caption font-extrabold uppercase tracking-wider"
          style={{ color: palette.secondaryColor }}
        >
          {view.language === "fr" ? "Message clé" : "Key message"}
        </h2>
        <p className="m-0 wrap-break-word text-ui-body font-semibold leading-relaxed @lg/visual-abstract:text-ui-highlight">
          {view.keyMessage}
        </p>
      </BentoCard>

      <BentoCard className="min-w-0" palette={palette} tone="muted">
        <p className="m-0 wrap-break-word text-footnote leading-relaxed text-ds-slate-600">
          <strong>
            {view.language === "fr" ? "À garder en tête :" : "Keep in mind:"}
          </strong>{" "}
          {view.caveat}
        </p>
      </BentoCard>

      <BentoCard className="min-w-0" palette={palette} tone="surface">
        <p className="m-0 wrap-break-word text-caption leading-relaxed text-ds-slate-600">
          {view.citation.text} · DOI:{" "}
          <span className="break-all">{view.citation.doi}</span> ·{" "}
          <a
            className="underline"
            href={view.citation.licenseUrl}
            rel="noreferrer"
            target="_blank"
          >
            {view.citation.licenseCode}
          </a>
        </p>
      </BentoCard>
    </article>
  );
}

/**
 * A two-button poll that acknowledges its own answer. The `useState` is UI
 * machinery, not domain state: "this widget has been clicked" means nothing
 * outside the widget, nothing is persisted, and neither page that mounts the
 * poster has any other reason to track it. The answer itself leaves through
 * `onAnswer`.
 *
 * The poster speaks the ARTIFACT's language, never the reader's, so the copy is
 * a ternary on `view.language` like every other string on it — the public page
 * is read by people who never signed in and carry no interface language.
 */
function UsefulnessPrompt({
  language,
  onAnswer,
  palette,
}: {
  language: VisualAbstractView["language"];
  onAnswer: (useful: boolean) => void;
  palette: VisualAbstractPalette;
}): React.ReactElement {
  const [isAnswered, setIsAnswered] = useState(false);

  if (isAnswered)
    return (
      <p
        className="m-0 mt-6 text-center text-footnote"
        style={{ color: palette.primaryColor }}
      >
        {language === "fr"
          ? "Merci pour votre retour."
          : "Thanks for your feedback."}
      </p>
    );

  return (
    <div className="mt-6 flex flex-col items-center gap-2">
      <p className="m-0 text-footnote" style={{ color: palette.primaryColor }}>
        {language === "fr"
          ? "Ce résumé visuel vous a-t-il été utile ?"
          : "Was this visual abstract useful?"}
      </p>
      <div className="flex gap-2">
        <Button
          leftIcon={<IconThumbUp />}
          onClick={() => {
            setIsAnswered(true);
            onAnswer(true);
          }}
          size="sm"
          type="button"
          variant="outline"
        >
          {language === "fr" ? "Oui" : "Yes"}
        </Button>
        <Button
          leftIcon={<IconThumbDown />}
          onClick={() => {
            setIsAnswered(true);
            onAnswer(false);
          }}
          size="sm"
          type="button"
          variant="outline"
        >
          {language === "fr" ? "Non" : "No"}
        </Button>
      </div>
    </div>
  );
}

export function VisualAbstract({
  actions,
  view,
}: {
  actions: VisualAbstractActions;
  view: VisualAbstractView;
}): React.ReactElement {
  const palette = visualAbstractPalette(view);
  return (
    // The public shell renders outside the app chrome with `viewport-fit=cover`,
    // so the poster starts at y=0 and the iOS status bar prints over the header
    // card. The insets pad the scroll container itself — `env()` directly, never
    // `var(--safe-area-top)`, for the same reason as every other notch-clearing
    // surface — and cost nothing where the insets are zero. The preview kind
    // stays untouched: it mounts inside surfaces that already clear the notch.
    <main
      className={`${
        actions.kind === "public"
          ? "h-full overflow-y-auto pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
          : "min-h-full"
      } w-full overscroll-y-contain font-dm-sans`}
      lang={view.language}
      style={{
        backgroundColor: palette.backgroundColor,
        color: palette.primaryColor,
      }}
    >
      <div className="@container/visual-abstract mx-auto w-full max-w-6xl p-3 @lg/visual-abstract:p-6">
        <VisualAbstractPoster palette={palette} view={view} />

        {actions.kind === "public" ? (
          <nav className="flex flex-col gap-3 pt-6">
            <div className="flex flex-col gap-3 @lg/visual-abstract:flex-row">
              <a
                className={`${buttonVariants({ size: "lg", variant: "primary" })} flex-1 no-underline`}
                href={actions.articleUrl}
                onClick={actions.onArticleClick}
                style={brandedButtonStyle(palette)}
              >
                {view.language === "fr"
                  ? "Lire l’article sur Juisci"
                  : "Read the article on Juisci"}
              </a>
              <Button
                leftIcon={<IconShare2 />}
                onClick={actions.onShare}
                size="lg"
                type="button"
                variant="outline"
              >
                {view.language === "fr" ? "Partager" : "Share"}
              </Button>
            </div>
            {/* Below the article CTA on purpose. The poster's job is to deliver
                the science, and an acquisition prompt that outranked the paper
                would degrade the thing being shared. */}
            {actions.signupUrl === null ? null : (
              <div className="flex flex-col items-center gap-2 border-t border-solid border-ds-slate-200 pt-4">
                <p className="m-0 text-center text-footnote text-ds-slate-600">
                  {view.language === "fr"
                    ? "Recevez des résumés visuels comme celui-ci, gratuitement"
                    : "Get visual abstracts like this one, free"}
                </p>
                <a
                  className={`${buttonVariants({ size: "md", variant: "outline" })} no-underline`}
                  href={actions.signupUrl}
                  onClick={actions.onSignupClick}
                >
                  {view.language === "fr"
                    ? "Créer un compte gratuit"
                    : "Create a free account"}
                </a>
              </div>
            )}
            <a
              className={`${buttonVariants({ size: "md", variant: "ghost" })} no-underline`}
              href={actions.reportUrl}
              style={{ color: palette.primaryColor }}
            >
              {view.language === "fr"
                ? "Signaler une inexactitude"
                : "Report an inaccuracy"}
            </a>
          </nav>
        ) : null}

        {actions.onUsefulness === null ? null : (
          <UsefulnessPrompt
            language={view.language}
            onAnswer={actions.onUsefulness}
            palette={palette}
          />
        )}

        {/* Owns its own top margin: the actions bar above it renders only in the
            public view, so relying on that sibling's padding left the disclaimer
            flush against the citation card in the preview. */}
        <p className="m-0 mt-6 pb-4 text-center text-caption text-ds-slate-600">
          {view.language === "fr"
            ? "Généré avec JuisciGPT. L’IA peut commettre des erreurs : vérifiez la source."
            : "Generated with JuisciGPT. AI can make mistakes: verify the source."}
        </p>
      </div>
    </main>
  );
}

export function VisualAbstractThumbnail({
  href,
  view,
}: {
  /** Null renders the teaser with no call to action — e.g. inside a share sheet. */
  href: string | null;
  view: VisualAbstractView;
}): React.ReactElement {
  const palette = visualAbstractPalette(view);
  return (
    <div
      className="@container/visual-abstract rounded-2xl p-2"
      style={{ backgroundColor: palette.backgroundColor }}
    >
      <div className="flex flex-col gap-3">
        <VisualAbstractHeader
          palette={palette}
          titleLevel="secondary"
          view={view}
        />
        <HeroBento palette={palette} view={view} />
      </div>
      {href === null ? null : (
        <Link
          className={`${buttonVariants({ size: "lg", variant: "primary" })} mt-3 w-full no-underline`}
          style={brandedButtonStyle(palette)}
          to={href}
        >
          {view.language === "fr"
            ? "Ouvrir en plein écran"
            : "Open full-screen preview"}
        </Link>
      )}
    </div>
  );
}
